<template>
  <v-app>
    <!-- Navbar -->
    <Navbar />
    
    <!-- Main Content -->
    <v-main>
      <router-view v-slot="{ Component }">
        <transition name="page" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </v-main>
    
    <!-- Footer -->
    <Footer />
    
    <!-- Mobile Bottom Navigation -->
    <MobileBottomNav />
    
    <!-- Chat is temporarily disabled across the application. -->
    <ChatButton v-if="features.chat && authStore.isAuthenticated && !isSchedulePage" />
    
    <!-- Chat Modal -->
    <ChatModal v-if="features.chat" />
    
    <!-- Password Change Modal -->
    <PasswordChangeModal
      v-model="showPasswordModal"
      @password-changed="handlePasswordChanged"
    />
    
    <!-- Global Snackbar -->
    <GlobalSnackbar />
    
    <!-- Expiry Warning -->
    <ExpiryWarning />
    
    <!-- Connection Status (Technical Works overlay) -->
    <ConnectionStatus />
  </v-app>
</template>

<script setup>
import { onMounted, onBeforeUnmount, defineAsyncComponent, ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useChatStore } from '@/stores/chat'
import { useSocketStore } from '@/stores/socket'
import { useSnackbarStore } from '@/stores/snackbar'
import { useSessionTimeout } from '@/composables/useSessionTimeout'
import { useExpiryWarning } from '@/composables/useExpiryWarning'
import { triggerForcedClientRefresh } from '@/utils/api'
import { useSeo } from '@/composables/useSeo'
import { features } from '@/config/features'

// Static components (always needed)
import Navbar from '@/components/layout/Navbar.vue'
import Footer from '@/components/layout/Footer.vue'
import MobileBottomNav from '@/components/layout/MobileBottomNav.vue'

// Async components (lazy loaded)
const ChatButton = defineAsyncComponent(() => 
  import('@/components/chat/ChatButton.vue')
)
const ChatModal = defineAsyncComponent(() => 
  import('@/components/chat/ChatModal.vue')
)
const PasswordChangeModal = defineAsyncComponent(() => 
  import('@/components/auth/PasswordChangeModal.vue')
)
const GlobalSnackbar = defineAsyncComponent(() => 
  import('@/components/common/GlobalSnackbar.vue')
)
const ExpiryWarning = defineAsyncComponent(() => 
  import('@/components/common/ExpiryWarning.vue')
)
const ConnectionStatus = defineAsyncComponent(() => 
  import('@/components/common/ConnectionStatus.vue')
)

// Stores and composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

// Hide chat FAB on schedule pages (those pages have their own + FAB)
const SCHEDULE_ROUTES = ['InstructorSchedules', 'InstructorSchedule', 'Schedule', 'InstructorDashboard']
const isSchedulePage = computed(() => SCHEDULE_ROUTES.includes(route.name))
const sessionTimeout = useSessionTimeout()
const chatStore = useChatStore()
const socketStore = useSocketStore()
const snackbarStore = useSnackbarStore()
const { setupExpiryInterceptor } = useExpiryWarning()
const { t, locale } = useI18n()
const { loadSeoData, injectGlobalSchema } = useSeo()

// Password modal state
const showPasswordModal = ref(false)

// Watch for temporary password
watch(() => authStore.hasTemporaryPassword, (hasTemp) => {
  if (hasTemp && authStore.isAuthenticated) {
    showPasswordModal.value = true
  }
}, { immediate: true })

const handlePasswordChanged = async () => {
  // Refresh user data to update isTemporaryPassword flag
  await authStore.verifyToken()
  showPasswordModal.value = false
}

let connectionAttempted = false
let forceRefreshTimeout = null
let permissionsSyncTimeout = null

const handleForceRefresh = (payload = {}) => {
  // Permission updates are synchronized in-place by current clients. Older
  // clients do not know this reason and reload once to receive the new bundle.
  if (payload?.reason === 'permissions_updated') return

  snackbarStore.warning(t('common.forceRefreshIncoming'), {
    timeout: 1500,
    location: 'top'
  })

  if (forceRefreshTimeout) {
    clearTimeout(forceRefreshTimeout)
  }

  forceRefreshTimeout = setTimeout(() => {
    triggerForcedClientRefresh(payload?.version || null)
  }, 800)
}

const handlePermissionsUpdated = () => {
  if (permissionsSyncTimeout) {
    clearTimeout(permissionsSyncTimeout)
  }

  permissionsSyncTimeout = setTimeout(async () => {
    permissionsSyncTimeout = null
    if (!authStore.isAuthenticated) return

    const verified = await authStore.verifyToken()
    if (!verified) return

    const canManageSchedules = authStore.user?.canViewAllSchedules === true
    snackbarStore.info(
      t(canManageSchedules
        ? 'common.schedulePermissionEnabled'
        : 'common.schedulePermissionDisabled'),
      { location: 'top', timeout: 4000 }
    )

    if (!canManageSchedules && route.meta?.requiresAllScheduleView) {
      await router.replace(authStore.getDefaultRouteForRole(authStore.user?.role))
    }
  }, 150)
}

onMounted(async () => {
  // Load styles after initial render
  import('@/styles/main.scss')

  // Load SEO data and inject the global LocalBusiness JSON-LD schema.
  // This runs once per session — data is cached server-side for 60s and
  // client-side in-memory afterwards.
  loadSeoData().then(() => injectGlobalSchema(locale.value)).catch(() => {})

  // Setup expiry warning interceptor
  setupExpiryInterceptor()
  
  // Initialize authentication
  await authStore.initAuth()
  
  // Initialize session monitoring
  sessionTimeout.watchAuthChanges()
  
  // Initialize socket connection if authenticated (only once)
  if (authStore.isAuthenticated && !socketStore.connected && !connectionAttempted) {
    connectionAttempted = true
    socketStore.connect()
    if (features.chat) {
      await chatStore.loadChats()
    }
  }
})

// Refresh JSON-LD when the user switches language so Google sees the
// proper localized name / description.
watch(() => locale.value, (newLocale) => {
  injectGlobalSchema(newLocale)
})

// Watch for auth changes (only connect once per auth state change)
watch(() => authStore.isAuthenticated, (isAuth, oldAuth) => {
  if (isAuth && !oldAuth && !socketStore.connected && !connectionAttempted) {
    connectionAttempted = true
    socketStore.connect()
    if (features.chat) {
      chatStore.loadChats()
    }
  } else if (!isAuth && oldAuth && socketStore.connected) {
    connectionAttempted = false
    socketStore.disconnect()
  }
})

watch(
  () => socketStore.socket,
  (newSocket, oldSocket) => {
    if (oldSocket) {
      oldSocket.off('force_refresh', handleForceRefresh)
      oldSocket.off('permissions_updated', handlePermissionsUpdated)
    }

    if (newSocket) {
      newSocket.on('force_refresh', handleForceRefresh)
      newSocket.on('permissions_updated', handlePermissionsUpdated)
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  // Clean disconnect when app unmounts
  if (socketStore.socket) {
    socketStore.socket.off('force_refresh', handleForceRefresh)
    socketStore.socket.off('permissions_updated', handlePermissionsUpdated)
  }
  if (socketStore.connected) {
    socketStore.disconnect()
  }
  if (forceRefreshTimeout) {
    clearTimeout(forceRefreshTimeout)
  }
  if (permissionsSyncTimeout) {
    clearTimeout(permissionsSyncTimeout)
  }
  connectionAttempted = false
})
</script>

<style>
:root {
  --app-viewport-height: 100vh;
  --app-safe-top: env(safe-area-inset-top, 0px);
  --app-safe-bottom: env(safe-area-inset-bottom, 0px);
}

/* Global styles */
html {
  overflow-y: auto;
  min-height: var(--app-viewport-height, 100vh);
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  min-height: var(--app-viewport-height, 100vh);
}

.v-application {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  min-height: var(--app-viewport-height, 100vh);
}

/* Smooth theme transition — applies to background / text / border color changes
   whenever the user switches themes (dark ↔ korkem). Excludes transform/opacity
   so animations on cards & buttons stay snappy. */
.v-application,
.v-application *,
.v-application *::before,
.v-application *::after {
  transition:
    background-color 0.35s ease,
    background 0.35s ease,
    color 0.35s ease,
    border-color 0.35s ease,
    box-shadow 0.35s ease,
    fill 0.35s ease,
    stroke 0.35s ease;
}

/* Disable theme transition for users who prefer reduced motion */
@media (prefers-reduced-motion: reduce) {
  .v-application,
  .v-application *,
  .v-application *::before,
  .v-application *::after {
    transition: none !important;
  }
}

/* Scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Custom animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in-up {
  animation: fadeInUp 0.6s ease-out;
}

.fade-in {
  animation: fadeIn 0.4s ease-out;
}

/* Utility classes */
.text-gradient {
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.glass-effect {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

/* Page transitions */
.page-enter-active,
.page-leave-active {
  transition: all 0.3s ease;
}

.page-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.page-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Global Mobile Responsiveness */
@media (max-width: 959px) {
  .v-main {
    padding-bottom: 72px !important;
  }
}

@media (max-width: 768px) {
  .v-container {
    padding: 0 16px !important;
  }
  
  /* Global text improvements */
  h1, h2, h3, h4, h5, h6 {
    line-height: 1.3 !important;
    word-wrap: break-word !important;
  }
  
  p, span, div {
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
  
  /* Ensure all text is visible */
  * {
    -webkit-font-smoothing: antialiased !important;
    -moz-osx-font-smoothing: grayscale !important;
  }
  
  /* Fix for disappearing text */
  .v-application {
    font-family: 'Roboto', sans-serif !important;
  }
  
  .v-btn {
    font-size: 0.95rem !important;
    white-space: normal !important;
    word-wrap: break-word !important;
  }
}

@media (max-width: 480px) {
  .v-container {
    padding: 0 12px !important;
  }
  
  h1 {
    font-size: 1.8rem !important;
  }
  
  h2 {
    font-size: 1.5rem !important;
  }
  
  h3 {
    font-size: 1.3rem !important;
  }
  
  h4 {
    font-size: 1.2rem !important;
  }
}
</style>
